/** Maintenance catalog + reminder status — ported 1:1 from the mobile app. */
import type { MaintenanceReminder } from "./model.ts";

export interface CatalogProcedure {
  key: string;
  nameEn: string;
  icon: string;
  defaultIntervalKm: number | null;
  defaultIntervalDays: number | null;
  vehicleTypes: string[];
  category: string;
}

const P = (key: string, nameEn: string, icon: string, km: number | null, days: number | null, category: string, vehicleTypes: string[] = ["car", "motorcycle"]): CatalogProcedure =>
  ({ key, nameEn, icon, defaultIntervalKm: km, defaultIntervalDays: days, vehicleTypes, category });

export const MAINTENANCE_CATALOG: CatalogProcedure[] = [
  P("oil_change", "Oil Change", "oil", 10000, 365, "Engine"),
  P("oil_filter", "Oil Filter", "filter", 10000, 365, "Engine"),
  P("air_filter", "Air Filter", "filter", 20000, 730, "Engine"),
  P("cabin_filter", "Cabin Air Filter", "filter", 15000, 365, "Engine", ["car"]),
  P("fuel_filter", "Fuel Filter", "filter", 30000, 1095, "Engine"),
  P("spark_plugs", "Spark Plugs", "spark", 40000, 1460, "Engine"),
  P("coolant", "Coolant Change", "coolant", 50000, 1825, "Engine"),
  P("timing_belt", "Timing Belt", "belt", 100000, 1825, "Engine"),
  P("serpentine_belt", "Serpentine Belt", "belt", 60000, 1095, "Engine"),
  P("water_pump", "Water Pump", "pump", 100000, 1825, "Engine"),
  P("thermostat", "Thermostat", "thermo", 80000, 1825, "Engine"),
  P("radiator_flush", "Radiator Flush", "coolant", 60000, 1095, "Engine"),
  P("brake_pads_front", "Brake Pads — Front", "brake", 30000, 730, "Brakes"),
  P("brake_pads_rear", "Brake Pads — Rear", "brake", 40000, 730, "Brakes"),
  P("brake_discs_front", "Brake Discs — Front", "disc", 60000, 1095, "Brakes"),
  P("brake_discs_rear", "Brake Discs — Rear", "disc", 70000, 1095, "Brakes"),
  P("brake_fluid", "Brake Fluid Flush", "fluid", 40000, 730, "Brakes"),
  P("parking_brake_adj", "Parking Brake Adjustment", "brake", 60000, null, "Brakes"),
  P("transmission_fluid_auto", "Transmission Fluid — Auto", "gear", 60000, 1095, "Transmission"),
  P("transmission_fluid_man", "Transmission Fluid — Manual", "gear", 80000, 1460, "Transmission"),
  P("differential_fluid", "Differential Oil", "gear", 80000, 1460, "Transmission"),
  P("transfer_case_fluid", "Transfer Case Fluid", "gear", 80000, 1460, "Transmission"),
  P("clutch", "Clutch Replacement", "clutch", 100000, null, "Transmission"),
  P("tyre_rotation", "Tire Rotation", "rotate", 10000, 365, "Tires", ["car"]),
  P("winter_tyres", "Winter Tire Change", "snow", null, 365, "Tires", ["car"]),
  P("summer_tyres", "Summer Tire Change", "sun", null, 365, "Tires", ["car"]),
  P("tyres_new", "New Tires", "tyre", 50000, 1825, "Tires"),
  P("wheel_alignment", "Wheel Alignment", "align", 20000, 365, "Tires"),
  P("tyre_pressure", "Tire Pressure Check", "pressure", 5000, 90, "Tires"),
  P("chain_lube", "Chain Lubrication", "chain", 500, 14, "Motorcycle", ["motorcycle"]),
  P("chain_replacement", "Chain Replacement", "chain", 20000, 730, "Motorcycle", ["motorcycle"]),
  P("chain_sprockets", "Chain & Sprockets Set", "sprocket", 25000, 730, "Motorcycle", ["motorcycle"]),
  P("chain_tension", "Chain Tension Adjustment", "tension", 1000, 30, "Motorcycle", ["motorcycle"]),
  P("motorcycle_oil", "Motorcycle Oil Change", "oil", 5000, 365, "Motorcycle", ["motorcycle"]),
  P("fork_oil", "Fork Oil Change", "fork", 20000, 730, "Motorcycle", ["motorcycle"]),
  P("brake_pads_moto", "Brake Pads — Motorcycle", "brake", 10000, 365, "Motorcycle", ["motorcycle"]),
  P("coolant_moto", "Coolant Change — Motorcycle", "coolant", 30000, 1095, "Motorcycle", ["motorcycle"]),
  P("air_filter_moto", "Air Filter — Motorcycle", "filter", 12000, 365, "Motorcycle", ["motorcycle"]),
  P("spark_plugs_moto", "Spark Plugs — Motorcycle", "spark", 15000, 730, "Motorcycle", ["motorcycle"]),
  P("valve_clearance", "Valve Clearance Check", "valve", 24000, 730, "Motorcycle", ["motorcycle"]),
  P("throttle_body_sync", "Throttle Body Sync", "sync", 24000, 730, "Motorcycle", ["motorcycle"]),
  P("suspension_bushes", "Suspension Bushes", "suspension", 80000, null, "Suspension"),
  P("shock_absorbers", "Shock Absorbers", "shock", 80000, 1825, "Suspension"),
  P("wheel_bearings", "Wheel Bearings", "bearing", 100000, null, "Suspension"),
  P("engine_mounts", "Engine Mounts", "mount", 100000, null, "Suspension"),
  P("battery", "Battery Replacement", "battery", null, 1460, "Electrical"),
  P("headlight_bulbs", "Headlight Bulbs", "light", null, 730, "Electrical"),
  P("glow_plugs", "Glow Plugs (Diesel)", "glow", 100000, null, "Electrical"),
  P("oxygen_sensor", "Oxygen / Lambda Sensor", "sensor", 100000, null, "Electrical"),
  P("a_c_service", "A/C Service", "ac", 40000, 730, "Climate"),
  P("a_c_filter", "A/C Pollen Filter", "filter", 15000, 365, "Climate"),
  P("fuel_injector", "Fuel Injector Cleaning", "injector", 50000, null, "Fuel & Exhaust"),
  P("egr_valve", "EGR Valve Cleaning", "egr", 80000, null, "Fuel & Exhaust"),
  P("dpf", "DPF / Particulate Filter", "dpf", 120000, null, "Fuel & Exhaust"),
  P("turbo", "Turbocharger Inspection", "turbo", 100000, null, "Fuel & Exhaust"),
  P("inspection", "Annual Inspection", "inspect", null, 365, "Inspection"),
  P("mot", "MOT / Safety Test", "cert", null, 365, "Inspection"),
  P("emissions_test", "Emissions Test", "emission", null, 365, "Inspection"),
  P("wiper_blades", "Wiper Blades", "wiper", null, 365, "General", ["car"]),
  P("power_steering_fluid", "Power Steering Fluid", "fluid", 60000, 1095, "General", ["car"]),
];

export function catalogFiltered(query: string, vehicleType?: string): CatalogProcedure[] {
  const eff = vehicleType === "motorcycle" ? "motorcycle" : (vehicleType && vehicleType !== "" ? "car" : null);
  const byType = eff == null ? MAINTENANCE_CATALOG : MAINTENANCE_CATALOG.filter((p) => p.vehicleTypes.includes(eff));
  const q = query.trim().toLowerCase();
  if (!q) return byType;
  return byType.filter((p) => p.nameEn.toLowerCase().includes(q) || p.key.toLowerCase().includes(q) || p.category.toLowerCase().includes(q));
}

export function intervalLabel(p: CatalogProcedure, metric: boolean): string {
  const parts: string[] = [];
  if (p.defaultIntervalKm != null) parts.push(`${Math.round(metric ? p.defaultIntervalKm : p.defaultIntervalKm * 0.621371)} ${metric ? "km" : "mi"}`);
  if (p.defaultIntervalDays != null) {
    const d = p.defaultIntervalDays;
    if (d >= 365) { const y = Math.floor(d / 365); const m = Math.floor((d % 365) / 30); parts.push(m > 0 ? `${y}y ${m}m` : `${y}y`); }
    else if (d >= 30) parts.push(`${Math.floor(d / 30)}m`);
    else parts.push(`${d}d`);
  }
  return parts.join(" / ");
}

/** Reminder type from a catalog procedure (mobile catalogReminderType). */
export function reminderTypeForProcedure(p: CatalogProcedure): string {
  if (["oil_change", "motorcycle_oil", "fork_oil"].includes(p.key)) return "oil_change";
  if (["tyres_new", "tyre_rotation", "winter_tyres", "summer_tyres"].includes(p.key)) return "tyre_change";
  if (p.category === "Inspection") return "service_appointment";
  return "custom";
}

export type ReminderStatus = "overdue" | "due_soon" | "on_track" | "completed";

export function reminderStatus(r: MaintenanceReminder, currentOdometer: number): ReminderStatus {
  if (r.isCompleted) return "completed";
  const nowSec = Date.now() / 1000;
  const dayStatus: ReminderStatus | null = r.dueDate != null
    ? (() => { const d = Math.floor((r.dueDate! - nowSec) / 86400); return d < 0 ? "overdue" : d <= (r.notifyBeforeDays ?? 7) ? "due_soon" : "on_track"; })()
    : null;
  const distStatus: ReminderStatus | null = r.dueOdometer != null
    ? (() => { const rem = r.dueOdometer! - currentOdometer; return rem < 0 ? "overdue" : rem <= (r.notifyBeforeDistance ?? 500) ? "due_soon" : "on_track"; })()
    : null;
  const both = [dayStatus, distStatus].filter((x) => x != null) as ReminderStatus[];
  if (both.includes("overdue")) return "overdue";
  if (both.includes("due_soon")) return "due_soon";
  return both[0] ?? "on_track";
}

export function reminderSubtitle(r: MaintenanceReminder, currentOdometer: number): string {
  const parts: string[] = [];
  if (r.dueDate != null) {
    const d = new Date(r.dueDate * 1000);
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    parts.push(`Due ${months[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`);
  }
  if (r.dueOdometer != null) {
    const rem = r.dueOdometer - currentOdometer;
    parts.push(rem > 0 ? `${Math.round(rem)} km to go` : `${Math.round(-rem)} km overdue`);
  }
  return parts.length ? parts.join(" · ") : (r.notes ?? "No schedule set");
}

const TYPE_NAMES: Record<string, string> = {
  oil_change: "Oil Change", tyre_change: "Tire Change", service_appointment: "Service Appointment", custom: "Custom",
};
export const reminderTypeName = (raw?: string) => TYPE_NAMES[raw ?? "custom"] ?? "Custom";
